import React from "react";
import { useNavigate } from "react-router-dom";

export default function WhatareHarmones() {
  const navigate = useNavigate();

  return (
    <div className="font-sans">

      {/* ================= HERO ================= */}
      <section className="bg-gradient-to-br from-pink-50 via-white to-purple-50 py-24 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold text-pink-700 leading-tight">
          What Are Hormones & <br className="hidden sm:block" />
          Why They Matter for Women
        </h1>

        <p className="mt-6 max-w-3xl mx-auto text-gray-700 text-lg md:text-xl">
          Hormones are powerful chemical messengers that influence how women feel, think, and function every single day —
          from energy levels and mood to metabolism, skin health, and reproductive balance.
        </p>
      </section>

      {/* ================= CONTENT ================= */}
      <section className="max-w-4xl mx-auto px-6 py-16 space-y-16 text-gray-700">

        {/* WHAT ARE HORMONES */}
        <div className="flex flex-col md:flex-row gap-6">
          <img
            src="/assets/Glow Picks (2).png"
            alt="Hormones"
            className="w-full md:w-1/4 rounded-xl shadow-lg"
          />
          <div>
            <h2 className="text-3xl font-bold text-pink-600 mb-4">
              What Are Hormones?
            </h2>
            <p className="text-lg leading-relaxed">
              Hormones are chemical messengers produced by glands such as the thyroid, adrenal glands, ovaries, and pancreas.
              They travel through the bloodstream and send signals to organs and tissues, guiding essential processes like
              metabolism, growth, mood regulation, sleep cycles, and reproductive health.
              <br /><br />
              Because women experience natural hormonal shifts during puberty, menstrual cycles, pregnancy, and menopause,
              even small imbalances can noticeably affect physical health, emotional wellbeing, and daily energy.
            </p>
          </div>
        </div>

        {/* WHY THEY MATTER */}
        <div>
          <h2 className="text-3xl font-bold text-pink-600 mb-4">
            Why Hormones Matter So Much for Women
          </h2>
          <p className="text-lg mb-4">
            Balanced hormones help the body function smoothly. When hormones are supported, women often feel more energized,
            emotionally stable, and physically resilient.
          </p>
          <ul className="list-disc list-inside text-lg space-y-2">
            <li><strong className="text-pink-500">Energy:</strong> Supports consistent stamina throughout the day</li>
            <li><strong className="text-pink-500">Mood:</strong> Helps regulate stress and emotional wellbeing</li>
            <li><strong className="text-pink-500">Metabolism:</strong> Assists with healthy weight management</li>
            <li><strong className="text-pink-500">Skin & Hair:</strong> Promotes natural glow and strength</li>
            <li><strong className="text-pink-500">Sleep:</strong> Encourages restful recovery at night</li>
          </ul>
        </div>

        {/* SIGNS */}
        <div>
          <h2 className="text-3xl font-bold text-pink-600 mb-4">
            Common Signs of Hormonal Imbalance
          </h2>
          <p className="text-lg mb-4">
            Hormonal imbalances can show up in subtle or noticeable ways. Common symptoms include:
          </p>
          <ul className="list-disc list-inside text-lg space-y-2">
            <li>Persistent fatigue or low energy</li>
            <li>Mood swings, anxiety, or irritability</li>
            <li>Acne or unexpected skin changes</li>
            <li>Hair thinning or excessive hair fall</li>
            <li>Unexplained weight changes</li>
            <li>Irregular menstrual cycles</li>
            <li>Difficulty sleeping</li>
          </ul>
        </div>

        {/* SUPPORT */}
        <div>
          <h2 className="text-3xl font-bold text-pink-600 mb-4">
            Supporting Hormonal Health Naturally
          </h2>
          <p className="text-lg mb-4">
            While hormones naturally fluctuate, healthy habits can support balance over time.
            Prioritizing sleep, managing stress, nourishing the body, and using targeted supplements
            can help the body maintain hormonal harmony.
          </p>
          <ul className="list-disc list-inside text-lg space-y-2">
            <li>Consistent, quality sleep</li>
            <li>Balanced meals with protein and healthy fats</li>
            <li>Stress management through mindfulness or movement</li>
            <li>Regular physical activity</li>
            <li>High-quality, evidence-based supplements</li>
          </ul>
        </div>

        {/* ================= SUPPLEMENT FEATURE ================= */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-pink-100">
          <h3 className="text-2xl font-bold text-pink-700 mb-4">
            Featured Supplement: Vitamin D
          </h3>

          <div className="flex flex-col md:flex-row gap-6 items-center">
            <img
              src="/assets/VITAMIND.webp"
              alt="Vitamin D Supplement"
              className="w-full md:w-1/3 rounded-xl shadow"
            />

            <div>
              <p className="text-lg mb-4">
                Vitamin D plays an important role in hormone regulation, immune function,
                and overall wellbeing. Low vitamin D levels have been linked to fatigue,
                mood changes, and hormonal irregularities in women.
                <br /><br />
                Supporting healthy vitamin D levels may help improve energy, emotional balance,
                and support reproductive and metabolic health — especially for women with limited
                sun exposure.
              </p>

              <a
                href="https://amzn.to/49JTRkq"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 bg-pink-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-pink-600 transition"
              >
                Shop Vitamin D on Amazon →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ================= DISCLAIMER ================= */}
      <section className="bg-gray-50 py-10 px-6 text-center">
        <p className="max-w-3xl mx-auto text-sm text-gray-600">
          This content is for educational purposes only and does not replace medical advice.
          Always consult a healthcare professional before starting supplements.
          As an Amazon Associate, Glow Picks may earn from qualifying purchases.
        </p>
      </section>

    </div>
  );
}
